// Availability per feature (plan §4.1, §6; IMPROVE-BO-06), derived from the contracts, never hand-coded: an operation
// declared `availability: 'cloud'` is not served by the location node, so the local backoffice shows "Needs internet"
// for it; a setting or field whose writes are `origin: 'cloud'` is read-only there. The same build in the cloud offers
// both. Connectivity (the server unreachable) is a separate, live signal.
import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Banner } from '@radish/ui-web';
import type { OperationDef, Origin } from '@radish/contracts';
import { useApi, CallFailure } from '../data/api.tsx';

type Availability = 'available' | 'needs-internet';

/** Whether this build, where it is served, can run the operation. */
export function useAvailability(def: Pick<OperationDef, 'availability'>): Availability {
  const { environment } = useApi();
  return environment.profile === 'location' && def.availability === 'cloud' ? 'needs-internet' : 'available';
}

/** Whether a write of this origin can be made here: cloud-origin fields are read-only on the node. */
export function useWritable(origin: Origin): boolean {
  const { environment } = useApi();
  return !(environment.profile === 'location' && origin === 'cloud');
}

export function NeedsInternet({ feature }: { readonly feature: string }) {
  return (
    <Banner tone="offline" testId="needs-internet">
      <p className="font-medium">Needs internet</p>
      <p>{feature} is managed online. It is available in the cloud Backoffice, or here once the store is back online.</p>
    </Banner>
  );
}

const subscribeOnline = (cb: () => void) => {
  window.addEventListener('online', cb);
  window.addEventListener('offline', cb);
  return () => {
    window.removeEventListener('online', cb);
    window.removeEventListener('offline', cb);
  };
};

/**
 * The connectivity indicator (IMPROVE-BO-05): hidden while healthy; a banner when the browser is offline or the last
 * call could not reach the server. It clears itself on the next successful call.
 */
export function ConnectivityBanner(): ReactNode {
  const online = useSyncExternalStore(subscribeOnline, () => navigator.onLine, () => true);
  const queryClient = useQueryClient();
  const [unreachable, setUnreachable] = useState(false);
  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event) => {
        if (event.type !== 'updated') return;
        const { status, error } = event.query.state;
        if (status === 'error' && error instanceof CallFailure && error.code === 'network_error') setUnreachable(true);
        if (status === 'success') setUnreachable(false);
      }),
    [queryClient],
  );
  if (online && !unreachable) return null;
  return (
    <div className="px-[18px] pt-2 min-[600px]:px-6">
      <Banner tone="offline" testId="connectivity-banner">
        {online ? "Can't reach the server. Changes you make are not saved until it is back." : "You're offline. Changes you make are not saved until you're back online."}
      </Banner>
    </div>
  );
}
